package org.teddapp.project.sync

import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.realtime.PostgresAction
import io.github.jan.supabase.realtime.RealtimeChannel
import io.github.jan.supabase.realtime.channel
import io.github.jan.supabase.realtime.postgresChangeFlow
import io.github.jan.supabase.realtime.realtime
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import org.teddapp.project.db.nowMillis

/** Tables dont un changement distant doit se voir tout de suite. */
private val LIVE_TABLES = listOf("timer_state", "tedd_event")

/** Temps laissé aux changements groupés pour arriver ensemble. */
private const val DEBOUNCE_MS = 300L

/** Court répit laissé aux flux pour se déclarer avant de rejoindre le canal. */
private const val SETTLE_MS = 200L

/** Attente initiale avant de retenter une connexion perdue. */
private const val RETRY_MIN_MS = 2_000L

/** Plafond de l'attente entre deux tentatives. */
private const val RETRY_MAX_MS = 60_000L

/**
 * Écoute les changements distants au lieu d'aller les chercher.
 *
 * Le battement de cinq secondes suffit à ce qui peut attendre. Il ne suffit pas à
 * un chronomètre : lancer une course sur le téléphone doit l'afficher sur
 * l'ordinateur pendant qu'elle tourne, pas au tour suivant.
 *
 * Le guetteur ne lit pas la charge utile du changement. Il annonce simplement qu'il
 * s'est passé quelque chose, et laisse le cycle court faire son travail : la
 * résolution des conflits vit à un seul endroit, éprouvé, et pas ici en double.
 */
class LiveWatcher(
    private val client: SupabaseClient,
    private val scope: CoroutineScope,
    /** Ce qu'on déclenche quand le distant a bougé. */
    private val onRemoteChange: suspend () -> Unit,
) {

    private var channel: RealtimeChannel? = null
    private var job: Job? = null

    /**
     * Ouvre l'écoute et la maintient : une coupure réseau ne l'éteint pas.
     *
     * Sans effet si elle tourne déjà — la bascule du réglage peut rappeler cette
     * fonction, et deux boucles pour un seul canal ne serviraient à rien.
     */
    fun start() {
        if (job?.isActive == true) return
        job = scope.launch {
            var wait = RETRY_MIN_MS
            while (isActive) {
                val ok = runCatching { listen() }.isSuccess
                // Une écoute qui rend la main a été coupée : on rouvre, en espaçant.
                wait = if (ok) RETRY_MIN_MS else (wait * 2).coerceAtMost(RETRY_MAX_MS)
                delay(wait)
            }
        }
    }

    /** Ferme l'écoute pour de bon. Le cycle court, lui, continue de tourner. */
    fun stop() {
        job?.cancel()
        job = null
        val open = channel ?: return
        channel = null
        scope.launch { runCatching { open.unsubscribe() } }
    }

    private suspend fun listen() = coroutineScope {
        val live = client.channel("tedd-live")
        channel = live
        var last = 0L

        // Une table n'est déclarée au canal qu'à l'intérieur de son flux, quand la
        // lecture démarre — or l'abonnement lit cette liste au moment de rejoindre.
        // Les deux se couraient après : le canal s'ouvrait sans aucune table, restait
        // connecté et muet, et le battement de quinze secondes masquait le silence.
        //
        // On lance donc les lectures d'abord, on attend qu'elles signalent leur départ,
        // puis on laisse un court répit avant de rejoindre. La déclaration se fait dans
        // le flux, juste après ce signal. La bibliothèque expose bien une inscription
        // explicite, mais elle est marquée interne : s'y appuyer casserait à la
        // première mise à jour, pour un gain que le battement couvre déjà.
        val prets = LIVE_TABLES.map { CompletableDeferred<Unit>() }
        val lecteurs = LIVE_TABLES.mapIndexed { index, name ->
            val flux = live.postgresChangeFlow<PostgresAction>(schema = "public") { table = name }
            launch {
                flux.onStart { prets[index].complete(Unit) }.collect {
                    // Une modification en amène souvent d'autres dans la foulée : on
                    // laisse le groupe arriver avant de lancer un cycle, plutôt qu'un
                    // cycle par ligne changée.
                    val now = nowMillis()
                    if (now - last < DEBOUNCE_MS) return@collect
                    last = now
                    delay(DEBOUNCE_MS)
                    runCatching { onRemoteChange() }
                    println("[live] $name a changé, cycle en ${nowMillis() - now} ms")
                }
            }
        }

        prets.forEach { it.await() }
        delay(SETTLE_MS)

        live.subscribe(blockUntilSubscribed = true)
        println("[live] abonné à ${LIVE_TABLES.joinToString(", ")}")
        lecteurs.forEach { it.join() }
        client.realtime.removeChannel(live)
    }

}
